"use client";

import React, { createContext, useState, useContext, ReactNode, useCallback, useMemo } from 'react';
import { sendAnalyticsEvent } from '@/components/AnalyticsListener';
import toast from 'react-hot-toast';
import { useStore } from './StoreContext';
import { formatCurrency } from '@/lib/formatters';
import { getSubdomain } from '@/lib/subdomain';
import { getApiBaseUrl } from '@/lib/api';

// --- INTERFACES ---
interface CartItem {
    id: number;
    name: string;
    price: string;
    imageUrl: string;
    quantity: number;
    type: 'product_variant' | 'service';
}

interface Coupon {
    code: string;
    description?: string;
    type: 'percentage' | 'fixed';
    value: number;
}

// --- TIPO DO CONTEXTO ---
interface CartContextType {
    isCartOpen: boolean;
    openCart: () => void;
    closeCart: () => void;
    cartItems: CartItem[];
    // ATUALIZADO: Agora aceita 'quantity' opcional
    addToCart: (item: Omit<CartItem, 'quantity'> & { quantity?: number }) => void;
    removeFromCart: (productId: number) => void;
    updateQuantity: (productId: number, quantity: number) => void;
    clearCart: () => void;
    totalItems: number;
    subTotal: number;
    appliedCoupon: Coupon | null;
    discountAmount: number;
    finalTotal: number;
    shippingCost: number;
    setShippingCost: (cost: number) => void;
    applyCoupon: (couponCode: string) => Promise<boolean>;
    removeCoupon: () => void;
    cartTotal: string; 
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider = ({ children }: { children: ReactNode }) => {
    const { settings } = useStore();
    const [isCartOpen, setIsCartOpen] = useState(false);
    const [cartItems, setCartItems] = useState<CartItem[]>([]);
    const [appliedCoupon, setAppliedCoupon] = useState<Coupon | null>(null);
    const [shippingCost, setShippingCost] = useState<number>(0);

    const openCart = useCallback(() => setIsCartOpen(true), []);
    const closeCart = useCallback(() => setIsCartOpen(false), []);

    // === ATUALIZADO: Lógica de Quantidade ===
    const addToCart = useCallback((itemToAdd: Omit<CartItem, 'quantity'> & { quantity?: number }) => {
        // Se a quantidade vier no item, usa ela. Se não, usa 1.
        const quantityToAdd = itemToAdd.quantity && itemToAdd.quantity > 0 ? itemToAdd.quantity : 1;

        setCartItems(prevItems => {
            const existingItem = prevItems.find(item => item.id === itemToAdd.id && item.type === itemToAdd.type);
            
            if (existingItem) {
                // Se já existe, soma a quantidade enviada (ex: tinha 2, adicionei 4 -> vira 6)
                return prevItems.map(item =>
                    item.id === itemToAdd.id && item.type === itemToAdd.type
                        ? { ...item, quantity: item.quantity + quantityToAdd }
                        : item
                );
            }
            
            // Se é novo, adiciona com a quantidade enviada
            const type = itemToAdd.type || 'product_variant';
            return [...prevItems, { ...itemToAdd, type, quantity: quantityToAdd }];
        });

        sendAnalyticsEvent('add_to_cart', {
            product_id: itemToAdd.id,
            product_name: itemToAdd.name,
            price: itemToAdd.price,
            quantity: quantityToAdd
        });
        
        // Mantemos sem toast aqui, pois cada tela chama seu toast personalizado
    }, []);

    const removeFromCart = useCallback((productId: number) => {
        setCartItems(prevItems => prevItems.filter(item => item.id !== productId));
    }, []);

    const updateQuantity = useCallback((productId: number, quantity: number) => {
        if (quantity <= 0) {
            removeFromCart(productId);
        } else {
            setCartItems(prevItems =>
                prevItems.map(item =>
                    item.id === productId ? { ...item, quantity } : item
                )
            );
        }
    }, [removeFromCart]);

    const clearCart = useCallback(() => {
        setCartItems([]);
        setAppliedCoupon(null);
        setShippingCost(0);
    }, []);

    const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);

    const subTotal = useMemo(() => {
        return cartItems.reduce((sum, item) => {
            const price = parseFloat(item.price);
            return sum + (price * item.quantity);
        }, 0);
    }, [cartItems]);

    const discountAmount = useMemo(() => {
        if (!appliedCoupon) return 0;

        if (appliedCoupon.type === 'percentage') {
            return subTotal * (appliedCoupon.value / 100);
        }
        if (appliedCoupon.type === 'fixed') {
            return appliedCoupon.value;
        }
        return 0;
    }, [appliedCoupon, subTotal]);

    const finalTotal = useMemo(() => {
        const total = subTotal - discountAmount + shippingCost;
        return total < 0 ? 0 : total;
    }, [subTotal, discountAmount, shippingCost]);
    
    const cartTotal = formatCurrency(finalTotal, settings.currency);

    const applyCoupon = useCallback(async (couponCode: string): Promise<boolean> => {
        if (!couponCode) return false;

        const loadingToast = toast.loading('Validando cupom...');
        try {
            const subdomain = getSubdomain();
            
            if (!subdomain) throw new Error("Subdomínio não identificado.");

            const apiBaseUrl = getApiBaseUrl();
            const response = await fetch(`${apiBaseUrl}/public/coupons/validate`, {
                method: 'POST',
                headers: {
                    'X-Tenant-Subdomain': subdomain,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({ code: couponCode })
            });
            
            const data = await response.json();
            
            toast.dismiss(loadingToast);

            if (!response.ok) {
                toast.error(data.message || 'Não foi possível aplicar o cupom.');
                return false;
            }

            setAppliedCoupon(data);
            toast.success(`Cupom "${data.code}" aplicado com sucesso!`);
            return true;

        } catch (err) {
            toast.dismiss(loadingToast);
            toast.error('Ocorreu um erro ao validar o cupom.');
            console.error(err);
            return false;
        }
    }, [subTotal]);

    const removeCoupon = useCallback(() => {
        setAppliedCoupon(null);
        toast.success('Cupom removido.');
    }, []);


    return (
        <CartContext.Provider value={{ 
            isCartOpen, openCart, closeCart, cartItems, addToCart, removeFromCart, 
            updateQuantity, clearCart, totalItems, cartTotal,
            subTotal, appliedCoupon, discountAmount, finalTotal, applyCoupon, removeCoupon,
            shippingCost, setShippingCost
        }}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => {
    const context = useContext(CartContext);
    if (context === undefined) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};